import { AnalyticsProvider as EventableAnalytics } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import Cookies from 'js-cookie';
import React, { Suspense } from 'react';
import '|/helpers/loadRUM';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux'; // Only needed until we can officially get rid of redux
import {
  RouterProvider,
  createBrowserRouter,
  useRouteError
} from 'react-router-dom';
import App from '|/app';
import { ModalContextProvider } from '|/app/modals';
import '|/index.css';
import '|/scripts/gtm-config';
import '|/scripts/xhr-extensions';
import '|/scripts/number-extensions';
import '@carvana/showroom-css-theme/Theme.css'; // We can get rid of this when we rewrite the components
import '@carvana/showroom-forms/index.css'; // We can get rid of this when we rewrite the components
import '@carvana/showroom-autocomplete/index.css'; // We can get rid of this when we rewrite the components
import '@carvana/header/styles.css';
import ErrorBoundary from '|/components/base/error-boundary';
import Loading from '|/components/base/loading';
import { ExperimentProvider } from '~/library/helpers/src/experiment/experiment/provider';
import store from '~/library/store/src/store'; // We can get rid of this when we get rid of redux
import Error from './components/base/error';

const root = document.getElementById('root');

enum RequestHeaderNames {
  appVersion = 'X-CVNA-AppVersion',
  bcid = 'X-CVNA-BrowserCookieId',
  deviceId = 'X-CVNA-DeviceId',
  deviceType = 'X-CVNA-DeviceType',
  platform = 'X-CVNA-Platform'
}

const createMobileRequestHeaders = () => {
  const headers: Partial<Record<RequestHeaderNames, string>> = {};

  return (Object.keys(RequestHeaderNames) as RequestHeaderNames[]).reduce(
    (acc, headerName) => {
      acc[headerName] = Cookies.get(headerName);

      return acc;
    },
    headers
  );
};

const checkForMobileCookies = (
  mobileHeaders = createMobileRequestHeaders()
) => {
  const { appVersion, platform } = RequestHeaderNames;

  return (
    mobileHeaders?.[appVersion] !== undefined ||
    mobileHeaders?.[platform] !== undefined
  );
};

const basename =
  window.location.pathname.includes('procurement') || checkForMobileCookies()
    ? '/procurement'
    : '/purchase';

const RouteError = () => {
  const error = useRouteError();
  datadogRum.addError(error, {
    customType: 'routeError',
    passedInError: error
  });

  return <Error error={error as Error} errorInfo={null} />;
};

const router = createBrowserRouter(
  [{ path: '/*', element: <App />, errorElement: <RouteError /> }],
  {
    basename: basename
  }
);

const Providers: React.FC<React.PropsWithChildren<{}>> = ({ children }) => (
  <Provider store={store}>
    <EventableAnalytics
      environment={process.env.NODE_ENV}
      projectName="PurchaseUI"
      track="TXN"
    >
      <ModalContextProvider>
        <ExperimentProvider
          defaults={{
            apiTimeout: 2000,
            cacheMinutes: 15,
            defaultBucket: 'control',
            environment: process.env.NODE_ENV
          }}
        >
          <ErrorBoundary>
            <Suspense fallback={<Loading />}>{children}</Suspense>
          </ErrorBoundary>
        </ExperimentProvider>
      </ModalContextProvider>
    </EventableAnalytics>
  </Provider>
);

if (root !== null) {
  ReactDOM.render(
    <Providers>
      <RouterProvider router={router} />
    </Providers>,
    root
  );
}
